import { useRef, useState, useEffect } from 'react';
import {
	DialogContent,
	Button,
	Divider,
	Paper,
	Tooltip,
	TextField,
	Grid,
	Box,
	IconButton,
	Chip,
} from '@mui/material';
import { RemoveCircleOutline, Save, MenuBookOutlined } from '@mui/icons-material';
import {
	MoleCancelButton,
	MoleDialog,
	MoleDialogActions,
	MoleDialogHeader,
	useMoleThemeColor,
} from '../../components/MoleDialogShell';
import { updateActiveTask, Util } from '../../util/Util';
import {
	mapTasksWithActivation,
	refreshEmptyActiveTaskDate,
} from '../../util/taskActivation';
import { loadPendingTasks, persistPendingTasks, revivePendingItems, useSkipFirstRun } from '../../util/pendingTaskStorage';
import { flushItemTableItems } from '../../util/itemTableFlush';
import ConfirmDialog from '../../components/ConfirmDialog';
import BomDetail from './BomDetail';
import TaskPane from '../../components/TaskPane';
import BomItemDetail from './BomItemDetail';
import { useAuth } from '../../hooks/useAuth';
import { toast } from 'react-toastify';
import { BomItem, BomTask, useBom } from './BomContext';
import { useUI } from '../../providers/MoleUIContext';
import { LocalStorageKeys, MYSQL_LIMITS } from '../../util/constants';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import ItemTable from '../../components/ItemTable';
import { useAppSocket } from '../../providers/AppSocketContext';

import { addMoney } from "../../util/quantity";
import { useEphemeralEditTask } from "../../hooks/useEphemeralEditTask";
interface Props {
	isOpen: boolean;
	toggle: () => void;
	bomId?: number | null;
	onSuccess?: () => void;
}

const BomModal = ({ isOpen, toggle, bomId, onSuccess }: Props) => {
	const [taskHash, setTaskHash] = useState<string | null>(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [renderChildren, setRenderChildren] = useState(false);
	const [tasksOpen, setTasksOpen] = useState(false);
	const [validateDetail, setValidateDetail] = useState(false);

	const { api, session } = useAuth();
	const { setLoadingMessage, queueReRun, t } = useUI();
	const { appSettings } = useAppSocket();
	const {
		pendingTasks,
		setPendingTasks,
		fetchedTask,
		setFetchedTask,
		activeTask,
		addNewTask,
		setItems,
		items,
		apiRef,
	} = useBom();

	// An existing document's draft is scratch work: it must not survive the
	// modal, saved or not, nor reach storage with the real drafts.
	useEphemeralEditTask({
		editing: Boolean(bomId),
		tasks: pendingTasks,
		setTasks: setPendingTasks,
	});
	const themeColor = useMoleThemeColor();

	const prevTaskIdRef = useRef<string | null>(null);
	const validateBtnRef = useRef<HTMLButtonElement>(null);
	const changeRef = useRef(false);
	const skipFirstPersist = useSkipFirstRun();

	const { createdAt, updatedAt, active, items: itms, ...bundleDetails } =
		activeTask || ({} as any);

	const bundleItems = (itms || []).map((row: BomItem) => ({ ...row }));

	const hasInput = !!activeTask?.items?.some((i) => i.role === 'INPUT');
	const hasOutput = !!activeTask?.items?.some((i) => i.role === 'OUTPUT');

	const canValidate =
		activeTask &&
		validateDetail &&
		(activeTask.items?.length || 0) > 0 &&
		hasInput &&
		hasOutput &&
		(bomId
			? taskHash !==
			Util.hashObject(
				JSON.stringify(bundleDetails) + JSON.stringify(bundleItems)
			)
			: true);

	const fetchData = async () => {
		if (!bomId) return;
		setLoadingMessage('FETCH');
		const res = await api.get(`/prod-boms/${bomId}`);
		if (res?.status === 200) {
			setFetchedTask(res.data);
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(fetchData);
		}
		setLoadingMessage(null);
	};

	const onSubmit = () => setConfirmModalOpen(true);

	const handleConfirm = async () => {
		if (!activeTask) return;

		const taskItems =
			flushItemTableItems<BomItem>(apiRef) ?? activeTask.items!;

		const components = taskItems.map((row, idx) => ({
			item: row.id,
			item_desc: row.item,
			role: row.role,
			quantity: row.quantity,
			unit: row.unit || '',
			amount: row.price || 0,
			sort_order: idx,
		}));

		const payload = {
			name: activeTask.name.trim(),
			code: (activeTask.code || '').trim() || null,
			description: activeTask.description || null,
			is_active: activeTask.is_active,
			depot: activeTask.depot > 0 ? activeTask.depot : null,
			components,
		};

		const isUpdate = Boolean(bomId);
		setLoadingMessage(isUpdate ? 'UPDATE' : 'SAVE');
		const res = isUpdate
			? await api.put(`/prod-boms/${bomId}`, payload)
			: await api.post('/prod-boms', payload);

		if (res && (res.status === 200 || res.status === 201)) {
			// Single success toast (create or update). Table refresh runs on dialog exit via changeRef.
			toast.success(
				t(isUpdate ? 'common:toast.success.bomUpdated' : 'common:toast.success.bom'),
				{ autoClose: 3000 }
			);
			changeRef.current = true;
			if (isUpdate) {
				toggle();
			} else {
				setPendingTasks((prev) => prev.filter((task) => task.id !== activeTask.id));
			}
		} else if (res?.status === 409) {
			toast.error(t('common:toast.error.integrity'), { autoClose: 3000 });
		}
		setLoadingMessage(null);
	};

	useEffect(() => {
		if (isOpen) {
			if (bomId) {
				addNewTask();
			} else {
				const parsed = loadPendingTasks(LocalStorageKeys.BOM_TASKS, (task: BomTask) =>
					refreshEmptyActiveTaskDate({
						...task,
						createdAt: new Date(task.createdAt),
						updatedAt: new Date(task.updatedAt),
						items: revivePendingItems(task.items, (it) => new BomItem(it)),
						total: function () {
							return (this.items || []).reduce(
								(sum: number, item: BomItem) => addMoney(sum, item.total),
								0
							);
						},
					}), session?.user_id);
				if (parsed) {
					setPendingTasks(parsed);
					return;
				}
				addNewTask();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	useEffect(() => {
		if (isOpen && !bomId) {
			const handleKeyDown = (e: KeyboardEvent) => {
				if (e.ctrlKey && e.altKey && e.key.toLowerCase() === 'q') {
					e.preventDefault();
					setTasksOpen((open) => !open);
				} else if (e.ctrlKey && e.altKey && e.key.toLowerCase() === 's') {
					e.preventDefault();
					validateBtnRef.current?.click();
				}
			};
			document.addEventListener('keydown', handleKeyDown);
			return () => document.removeEventListener('keydown', handleKeyDown);
		}
	}, [isOpen, bomId]);

	useEffect(() => {
		if (skipFirstPersist()) return;
		if (!bomId && isOpen) {
			if (pendingTasks.length === 0) {
				addNewTask();
				return;
			}
			persistPendingTasks(
				LocalStorageKeys.BOM_TASKS,
				session?.user_id,
				JSON.stringify(pendingTasks)
			);
		}
		if (pendingTasks.length > 0 && !activeTask) {
			setPendingTasks((prev) =>
				mapTasksWithActivation(prev, (_task, index) => index === 0)
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pendingTasks]);

	useEffect(() => {
		if (activeTask) {
			if (!renderChildren) setRenderChildren(true);
			if (prevTaskIdRef.current !== activeTask.id) {
				if (bomId) fetchData();
				prevTaskIdRef.current = activeTask.id;
			}
			if (
				bomId &&
				activeTask &&
				(activeTask.items?.length || 0) > 0 &&
				!taskHash
			) {
				setTaskHash(
					Util.hashObject(
						JSON.stringify(bundleDetails) + JSON.stringify(bundleItems)
					)
				);
			}
		} else {
			prevTaskIdRef.current = null;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeTask]);

	const cleanup = () => {
		setPendingTasks([]);
		setFetchedTask(null);
		setItems([]);
		setTaskHash(null);
		setRenderChildren(false);
		setConfirmModalOpen(false);
		prevTaskIdRef.current = null;
		// Refresh BOM table only after a successful server write and dialog close.
		if (changeRef.current) onSuccess?.();
		changeRef.current = false;
	};

	const onDeleteClicked = (uniqueId: string) => {
		updateActiveTask(setPendingTasks, (prev) => ({
			items: prev.items
				? prev.items.filter((item) => item.uniqueId !== uniqueId)
				: [],
		}));
	};

	const columns: GridColDef<BomItem>[] = [
		{
			field: 'rowNumber',
			headerName: '#',
			width: 20,
			valueGetter: (_, row) =>
				apiRef.current!.getRowIndexRelativeToVisibleRows(row.uniqueId) + 1,
		},
		{
			field: 'role',
			headerName: t('common:production.role'),
			width: 120,
			editable: true,
			type: 'singleSelect',
			valueOptions: [
				{ value: 'INPUT', label: t('common:production.input') },
				{ value: 'OUTPUT', label: t('common:production.output') },
			],
			renderCell: (params) => (
				<Chip
					size="small"
					label={
						params.value === 'OUTPUT'
							? t('common:production.output')
							: t('common:production.input')
					}
					color={params.value === 'OUTPUT' ? 'success' : 'default'}
					variant="outlined"
				/>
			),
		},
		...(Util.isBoolSettingEnabled(appSettings?.UI_SHOW_ITEM_CODE?.value)
			? [
				{
					field: 'item_code',
					headerName: t('common:tables.itemCode'),
				},
			]
			: []),
		{
			field: 'item',
			headerName: t('common:tables.item'),
			flex: 2,
			minWidth: 220,
		},
		{
			field: 'quantity',
			headerName: t('common:tables.quantity'),
			type: 'number',
			editable: true,
		},
		{
			field: 'unit',
			headerName: t('common:production.unit'),
			editable: true,
		},
		{
			field: 'price',
			headerName: t('common:tables.unitCost'),
			type: 'number',
			editable: true,
		},
		{
			field: 'total',
			headerName: t('common:tables.lineTotal'),
			type: 'number',
			flex: 1,
			minWidth: 150,
			maxWidth: 150,
		},
		{
			field: 'actions',
			headerName: '',
			sortable: false,
			disableColumnMenu: true,
			renderCell: (params: GridRenderCellParams<BomItem>) => (
				<Tooltip title={t('common:ui.tooltips.delete')}>
					<IconButton
						color="error"
						onClick={() => onDeleteClicked(params.row.uniqueId)}
					>
						<RemoveCircleOutline />
					</IconButton>
				</Tooltip>
			),
		},
	];

	return (
		<Box>
			<MoleDialog
				open={isOpen}
				onClose={toggle}
				maxWidth="xl"
				fullWidth
				themeColor={themeColor}
				paperOptions={{ maxWidth: '95%', width: '95%', maxHeight: '95vh' }}
				disableEscapeKeyDown
				onTransitionExited={cleanup}
			>
				<MoleDialogHeader
					title={
						bomId
							? t('common:ui.labels.editBom')
							: t('common:productionModal.bomTitle')
					}
					subtitle={
						activeTask
							? `${t('common:production.inputs')}: ${activeTask.items?.filter((i) => i.role === 'INPUT').length || 0
							} · ${t('common:production.outputs')}: ${activeTask.items?.filter((i) => i.role === 'OUTPUT').length ||
							0
							}`
							: undefined
					}
					icon={<MenuBookOutlined />}
					onClose={toggle}
					themeColor={themeColor}
					extra={
						!bomId ? (
							<Button
								variant="outlined"
								size="small"
								startIcon={<PendingActionsIcon />}
								onClick={() => setTasksOpen(true)}
							>
								{t('common:ui.buttons.tasks')}
							</Button>
						) : undefined
					}
				/>
				<DialogContent>
					<Paper elevation={24}>
						{renderChildren && (
							<BomDetail setValidateCheck={setValidateDetail} />
						)}
						<Divider orientation="vertical" variant="middle" flexItem />
						{renderChildren && <BomItemDetail />}
					</Paper>
					{renderChildren && (
						<ItemTable<BomItem, BomTask>
							activeTask={activeTask}
							setPendingTasks={setPendingTasks}
							columns={columns}
							itemConstructor={BomItem}
							bundle={fetchedTask?.components}
							items={items}
							checkStock={false}
							lineNameMode="strict"
							apiRef={apiRef}
						/>
					)}
				</DialogContent>
				<MoleDialogActions sx={{ justifyContent: 'stretch' }}>
					<Grid container spacing={1} alignItems="center" width="100%">
						<Grid sx={{ flexGrow: 1, minWidth: 0 }}>
							<TextField
								id="details"
								size="small"
								label={t('common:ui.labels.notes')}
								variant="filled"
								fullWidth
								type="text"
								value={activeTask?.description || ''}
								inputProps={{ maxLength: MYSQL_LIMITS.TEXT.maxLength }}
								onChange={(e) =>
									updateActiveTask(setPendingTasks, {
										description: e.target.value,
									})
								}
								disabled={!activeTask}
							/>
						</Grid>
						<Grid
							sx={{
								width: { xs: '100%', md: 'auto' },
								display: 'flex',
								justifyContent: { xs: 'space-between', md: 'flex-end' },
								alignItems: 'center',
								gap: 1,
							}}
						>
							<Button
								ref={validateBtnRef}
								variant="contained"
								color="success"
								onClick={onSubmit}
								disabled={!canValidate}
								startIcon={<Save />}
							>
								{t('common:ui.buttons.saveBom')}
							</Button>
							<MoleCancelButton onClick={toggle}>
								{t('common:ui.buttons.cancel')}
							</MoleCancelButton>
						</Grid>
					</Grid>
				</MoleDialogActions>

				{!bomId && (
					<TaskPane<BomItem, BomTask>
						tasks={pendingTasks}
						setTasks={setPendingTasks}
						addNewTask={addNewTask}
						tasksOpen={tasksOpen}
						onCallback={() => setTasksOpen(false)}
					/>
				)}
			</MoleDialog>

			<ConfirmDialog
				onConfirm={handleConfirm}
				isOpen={confirmModalOpen}
				setIsOpen={setConfirmModalOpen}
				title={t('common:production.bomSaveConfirm')}
				confirmBtnColor="success"
			/>
		</Box>
	);
};

export default BomModal;
